import { useState } from "react";
import { Link } from "react-router-dom";
import { Wrench, ArrowLeft, Send, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const CONTACT_EMAIL = "privacy@ironlog.au";
const EFFECTIVE = "July 2026";

function PrivacyContactForm() {
  const [form, setForm] = useState({ name: "", email: "", message: "", company_url: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) { toast.error("Please add your name, email and message."); return; }
    setSending(true);
    try { await api.post("/public/privacy-request", form); setDone(true); }
    catch (err) {
      if (err.response?.status === 429) toast.error("Too many requests — please try again shortly.");
      else toast.error("Something went wrong — please email us directly.");
    } finally { setSending(false); }
  };

  if (done) return (
    <div className="card p-6 mt-4 flex items-center gap-3" data-testid="privacy-form-success">
      <CheckCircle2 className="w-8 h-8 text-[#00E676] shrink-0" />
      <p className="font-body text-[#C7CED8]">Thanks — your message has been sent. We&apos;ll be in touch.</p>
    </div>
  );

  return (
    <form onSubmit={submit} className="card p-5 mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="privacy-form">
      <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", overflow: "hidden" }}>
        <label>Company URL<input type="text" tabIndex={-1} autoComplete="off" value={form.company_url} onChange={(e) => setForm({ ...form, company_url: e.target.value })} data-testid="privacy-hp" /></label>
      </div>
      <div><label className="il-label">Name</label><input className="il-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="privacy-name" /></div>
      <div><label className="il-label">Email</label><input className="il-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="privacy-email" /></div>
      <div className="sm:col-span-2"><label className="il-label">Message</label><textarea className="il-input py-3" style={{ minHeight: "6rem" }} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} required data-testid="privacy-message" /></div>
      <div className="sm:col-span-2"><button disabled={sending} className="btn-signal h-12 px-5 font-head flex items-center gap-2 disabled:opacity-60" data-testid="privacy-submit"><Send className="w-4 h-4" /> {sending ? "Sending…" : "Send Message"}</button></div>
    </form>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-10">
      <h2 className="font-head font-900 uppercase tracking-tight text-xl md:text-2xl text-white mb-3">{title}</h2>
      <div className="font-body text-[#C7CED8] leading-relaxed space-y-3">{children}</div>
    </section>
  );
}

export default function Privacy() {
  return (
    <div className="grain relative bg-[#0D0E11] min-h-screen text-white" data-testid="privacy-page">
      <header className="border-b border-white/10">
        <div className="max-w-[900px] mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="privacy-home-link">
            <div className="w-8 h-8 bg-[#FFB800] flex items-center justify-center"><Wrench className="w-5 h-5 text-black" strokeWidth={2.5} /></div>
            <span className="font-head font-900 text-2xl tracking-tight">IRONLOG</span>
          </Link>
          <Link to="/" className="font-mono text-xs text-[#94A3B8] hover:text-[#FFB800] flex items-center gap-2" data-testid="privacy-back-link">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>
        </div>
      </header>

      <main className="max-w-[900px] mx-auto px-6 py-14">
        <p className="font-mono text-xs uppercase tracking-widest text-[#FFB800] mb-3">Legal</p>
        <h1 className="font-head font-900 uppercase tracking-tight text-4xl sm:text-5xl mb-3">Privacy Policy</h1>
        <p className="font-mono text-sm text-[#94A3B8] mb-12">Effective {EFFECTIVE}</p>

        <Section title="1. Who We Are">
          <p>
            IronLog (&quot;we&quot;, &quot;us&quot;, &quot;our&quot;) provides machinery maintenance and fleet management software for farmers,
            contractors, councils, golf courses and landscapers. This policy explains what information we collect,
            how we use it, and the choices you have.
          </p>
        </Section>

        <Section title="2. Information We Collect">
          <p>We collect information you provide directly to us, including:</p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li><span className="text-white font-600">Account details</span> — your name, email address and role (manager or worker).</li>
            <li><span className="text-white font-600">Fleet &amp; machine data</span> — machines you add (make, model, serial number, year, running hours, odometer/kilometres, registration details, photos and notes), service logs and reminders.</li>
            <li><span className="text-white font-600">Team information</span> — fleet membership and invite codes used to join a fleet.</li>
            <li><span className="text-white font-600">Billing information</span> — plan selection and payment status. Card payments are processed securely by Stripe; we do not store your full card details.</li>
          </ul>
        </Section>

        <Section title="3. How We Use Your Information">
          <ul className="list-disc pl-6 space-y-1.5">
            <li>To provide, operate and maintain the IronLog service.</li>
            <li>To manage your fleet, machines, service history and reminders.</li>
            <li>To process payments and manage your subscription plan.</li>
            <li>To send you service-related emails and in-app notifications (e.g. welcome, access changes, cancellations, registration reminders).</li>
            <li>To secure the platform, prevent abuse and comply with legal obligations.</li>
          </ul>
        </Section>

        <Section title="4. QR Codes & Publicly Accessible Data">
          <p className="border-l-2 border-[#FFB800] pl-4 bg-white/[0.03] py-3">
            IronLog generates QR codes for machines. When you print or attach a machine&apos;s QR code, scanning it opens a
            <span className="text-white font-600"> public machine record</span> that is viewable by anyone who scans it —
            including individuals who do not have an IronLog account or an active subscription (&quot;non-active users&quot;).
            This public record shows limited, non-sensitive information: make, model, machine type, year, running hours /
            odometer, the machine photo (if one has been added), and a service history summary (number of services, last
            service date and recent service types/dates). Sensitive details — serial number, registration/rego number,
            service costs, private notes and the names of people who added or logged work — are <span className="text-white font-600">never</span> shown
            on the public record. If you attach a QR code to a machine, please be aware it may be scanned by third parties.
          </p>
        </Section>

        <Section title="5. How We Share Information">
          <p>We do not sell your personal information. We share data only:</p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li>With other members of your fleet (managers and workers can see shared fleet, machine and service data).</li>
            <li>With trusted service providers who help us operate the platform (e.g. payment processing via Stripe, email delivery, and our cloud hosting provider).</li>
            <li>Where required by law, or to protect the rights, safety and security of IronLog and its users.</li>
            <li>Via QR codes, as described in Section 4, where and when that feature is enabled.</li>
          </ul>
        </Section>

        <Section title="6. Data Storage, Security & Liability">
          <p>
            Your data is stored on secure, managed cloud infrastructure and is encrypted at rest. We apply
            reasonable technical and organisational measures to protect your information. Automated backups are
            maintained by our hosting provider to support recovery in the event of an outage.
          </p>
          <p className="border-l-2 border-[#FFB800] pl-4 bg-white/[0.03] py-3">
            IronLog relies on third-party cloud hosting and infrastructure providers to store and safeguard data.
            To the maximum extent permitted by law, IronLog is <span className="text-white font-600">not responsible or
            liable for any loss, corruption, or unavailability of data</span> that occurs on the underlying servers or
            hosting infrastructure. Responsibility for the operation, integrity, and disaster recovery of that
            infrastructure lies with the hosting provider, not with IronLog. We strongly encourage you to keep your
            own regular backups (see Section 8).
          </p>
        </Section>

        <Section title="7. Payments & Credit Card Details">
          <p>
            All card payments are processed by <span className="text-white font-600">Stripe</span>. Any credit card
            details you provide are collected, stored, and processed by Stripe in accordance with{" "}
            <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-[#FFB800] hover:underline">Stripe&apos;s Privacy Policy</a>{" "}
            and Stripe&apos;s PCI-DSS obligations. IronLog does not store or have access to your full card details, and
            <span className="text-white font-600"> responsibility for the security and handling of that payment
            information lies with Stripe</span>. Please review Stripe&apos;s Privacy Policy for details on how your
            payment data is managed.
          </p>
        </Section>

        <Section title="8. Data Export & Your Backups">
          <p>
            You can export a complete copy of your fleet data at any time from <span className="text-white font-600">Settings → Data Backup</span>,
            in JSON or CSV format. We encourage you to keep your own regular backups.
          </p>
        </Section>

        <Section title="9. Data Retention">
          <p>
            We retain your information for as long as your account is active. If your account or fleet is removed,
            associated machine, service and reminder data is deleted. Some records may be retained where required
            for legal, accounting or security purposes.
          </p>
        </Section>

        <Section title="10. Your Rights">
          <p>
            You may request access to, correction of, or deletion of your personal information. To exercise these
            rights, contact us using the details below. This policy is governed by the laws of Australia.
          </p>
        </Section>

        <Section title="11. Changes to This Policy">
          <p>
            We may update this policy from time to time. Material changes will be communicated through the app or
            by email. The &quot;Effective&quot; date above reflects the latest version.
          </p>
        </Section>

        <Section title="12. Contact Us">
          <p>
            For any privacy questions or requests, contact us at{" "}
            <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#FFB800] hover:underline" data-testid="privacy-contact-email">{CONTACT_EMAIL}</a>, or send us a message below.
          </p>
          <PrivacyContactForm />
        </Section>

        <div className="border-t border-white/10 pt-6 mt-4 font-mono text-xs text-[#94A3B8]">
          © {new Date().getFullYear()} IRONLOG · Built rugged in AUD
        </div>
      </main>
    </div>
  );
}
